import { useEffect, useState, useMemo } from 'react';
import {
    ResponsiveContainer, PieChart, Pie, Cell, Tooltip
} from 'recharts';
import { getPlantList } from "../api/plant";
import { getDashboardAnalytics } from "../api/others";

// ---- design tokens ----
const colors = {
    bg: '#0b1220',
    surface: '#121a2b',
    surfaceRaised: '#182136',
    border: '#232d42',
    textPrimary: '#eef1f7',
    textSecondary: '#8b96ab',
    textMuted: '#5a6479',
    accent: '#2dd4bf',     // teal — "live system"
    accentDim: '#0f766e',
    warn: '#f5a623',       // amber — secondary highlight
};

const SERIES = ['#2dd4bf', '#f5a623', '#7c85f0', '#e8698a', '#5da8e8', '#8fbf5a'];

// Injected once — styles every element with className="scroll-box" to match
// the dark theme instead of the OS default scrollbar.
const scrollbarCSS = `
  .scroll-box {
    scrollbar-width: thin;
    scrollbar-color: ${colors.border} transparent;
  }
  .scroll-box::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  .scroll-box::-webkit-scrollbar-track {
    background: transparent;
  }
  .scroll-box::-webkit-scrollbar-thumb {
    background-color: ${colors.border};
    border-radius: 3px;
  }
  .scroll-box::-webkit-scrollbar-thumb:hover {
    background-color: ${colors.accent};
  }
`;

function todayStr() {
    // local YYYY-MM-DD, not UTC
    const d = new Date();
    const offset = d.getTimezoneOffset();
    return new Date(d.getTime() - offset * 60000).toISOString().slice(0, 10);
}

export default function AnalyticsDashboard() {
    const [summary, setSummary] = useState(null);
    const [browsers, setBrowsers] = useState([]);
    const [osData, setOsData] = useState([]);
    const [devices, setDevices] = useState([]);
    const [topPages, setTopPages] = useState([]);
    const [recentActivity, setRecentActivity] = useState([]);
    const [visitsByUser, setVisitsByUser] = useState([]);
    const [plants, setPlants] = useState([]);
    const [selectedDate, setSelectedDate] = useState(todayStr());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const isToday = selectedDate === todayStr();

    useEffect(() => {
        async function load() {
            setLoading(true);
            setError(null);
            try {
                const response = await getDashboardAnalytics(selectedDate);
                const data = response?.data;

                if (!data) {
                    setError('Failed to load analytics data');
                    return;
                }

                setSummary(data.summary);
                setBrowsers(data.browsers || []);
                setOsData(data.os || []);
                setDevices(data.devices || []);
                setTopPages(data.topPages || []);
                setRecentActivity(data.recentActivity || []);
                setVisitsByUser(data.visitsByUser || []);

                // plant list only needs to be fetched once, not per date change
                if (plants.length === 0) {
                    const plantList = await getPlantList();
                    setPlants(plantList?.data ?? plantList ?? []);
                }
            } catch (err) {
                setError('Failed to load analytics data');
                console.error(err);
            } finally {
                setLoading(false);
            }
        }
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate]);

    // id -> company_name lookup
    const plantNameById = useMemo(() => {
        const map = new Map();
        for (const p of plants) {
            map.set(p.client_id, p.company_name);
        }
        return map;
    }, [plants]);

    // resolve user_id -> company name, server-aggregated counts
    const userVisits = useMemo(() => {
        return visitsByUser
            .map((row) => ({
                user: row.user_id == null
                    ? 'Not logged in'
                    : (plantNameById.get(row.user_id) || `User ${row.user_id}`),
                visits: row.visits,
            }))
            .sort((a, b) => b.visits - a.visits);
    }, [visitsByUser, plantNameById]);

    const maxUserVisits = userVisits.length ? userVisits[0].visits : 0;

    if (loading) return <div style={s.page}><style>{scrollbarCSS}</style><div style={s.loadingText}>Loading analytics…</div></div>;
    if (error) return <div style={s.page}><style>{scrollbarCSS}</style><div style={s.loadingText}>{error}</div></div>;

    return (
        <div style={s.page}>
            <style>{scrollbarCSS}</style>
            <header style={s.header}>
                <div>
                    <h1 style={s.h1}>Site analytics</h1>
                    <p style={s.subtitle}>Usage for {selectedDate}</p>
                </div>
                <div style={s.headerRight}>
                    <input
                        type="date"
                        value={selectedDate}
                        max={todayStr()}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        style={s.datePicker}
                    />
                    <div style={s.liveBadge}>
                        <span style={s.livePulse} />
                        {isToday ? 'Tracking active' : 'Historical view'}
                    </div>
                </div>
            </header>

            {/* KPI row */}
            <div style={s.kpiRow}>
                <StatCard label={isToday ? "Active users today" : "Active users"} value={summary?.activeUsersToday} />
                <StatCard label={isToday ? "Page views today" : "Page views"} value={summary?.pageViewsToday} />
            </div>

            {/* Breakdowns */}
            <div style={s.chartsRow}>
                <Panel title="Browser" flex={1}>
                    <DonutWithLegend data={browsers} nameKey="browser" valueKey="visits" />
                </Panel>

                <Panel title="OS" flex={1}>
                    <DonutWithLegend data={osData} nameKey="os" valueKey="visits" />
                </Panel>

                <Panel title="Device type" flex={1}>
                    <DonutWithLegend data={devices} nameKey="device_type" valueKey="visits" />
                </Panel>
            </div>

            <div style={s.chartsRow}>
                <Panel title="Visits by user" flex={1}>
                    <div style={s.scrollBox} className="scroll-box">
                        {userVisits.map((row) => (
                            <div key={row.user} style={s.siteRow}>
                                <div style={s.siteRowTop}>
                                    <span style={s.siteName}>{row.user}</span>
                                    <span style={s.siteCount}>{row.visits}</span>
                                </div>
                                <div style={s.siteBarTrack}>
                                    <div
                                        style={{
                                            ...s.siteBarFill,
                                            width: maxUserVisits
                                                ? `${(row.visits / maxUserVisits) * 100}%`
                                                : '0%',
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                        {userVisits.length === 0 && (
                            <div style={s.emptyRow}>No visits recorded for this date</div>
                        )}
                    </div>
                </Panel>

                <Panel title="Top pages" flex={1}>
                    <div style={s.scrollBox} className="scroll-box">
                        <table style={s.table}>
                            <thead>
                                <tr>
                                    <th style={s.th}>Page</th>
                                    <th style={{ ...s.th, textAlign: 'right' }}>Views</th>
                                    <th style={{ ...s.th, textAlign: 'right' }}>Avg. time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topPages.map((row) => (
                                    <tr key={row.page_url}>
                                        <td style={{ ...s.td, ...s.mono }}>{row.page_url}</td>
                                        <td style={{ ...s.td, textAlign: 'right' }}>{row.views}</td>
                                        <td style={{ ...s.td, textAlign: 'right', ...s.mono, color: colors.textSecondary }}>
                                            {row.avg_seconds != null ? `${row.avg_seconds}s` : '–'}
                                        </td>
                                    </tr>
                                ))}
                                {topPages.length === 0 && (
                                    <tr><td style={s.emptyRow} colSpan={3}>No page views recorded for this date</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Panel>
            </div>

            <div style={s.chartsRow}>
                <Panel title="Recent activity" flex={1}>
                    <div style={s.scrollBox} className="scroll-box">
                        <table style={s.table}>
                            <thead>
                                <tr>
                                    <th style={s.th}>Page</th>
                                    <th style={s.th}>Browser / OS</th>
                                    <th style={s.th}>IP address</th>
                                    <th style={{ ...s.th, textAlign: 'right' }}>Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentActivity.map((row, i) => (
                                    <tr key={i}>
                                        <td style={{ ...s.td, ...s.mono }}>{row.page_url}</td>
                                        <td style={{ ...s.td, color: colors.textSecondary, fontSize: 12 }}>
                                            {row.browser} · {row.os}
                                        </td>
                                        <td style={{ ...s.td, ...s.mono, color: colors.textSecondary }}>{row.ip_address ?? '–'}</td>
                                        <td style={{ ...s.td, textAlign: 'right', ...s.mono, fontSize: 12, color: colors.textMuted }}>
                                            {formatTime(row.visited_at)}
                                        </td>
                                    </tr>
                                ))}
                                {recentActivity.length === 0 && (
                                    <tr><td style={s.emptyRow} colSpan={4}>No activity recorded for this date</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Panel>
            </div>
        </div>
    );
}

function StatCard({ label, value }) {
    return (
        <div style={s.card}>
            <div style={s.cardValue}>{value ?? '–'}</div>
            <div style={s.cardLabel}>{label}</div>
        </div>
    );
}

function Panel({ title, children, flex }) {
    return (
        <div style={{ ...s.panel, flex }}>
            <h2 style={s.panelTitle}>{title}</h2>
            {children}
        </div>
    );
}

function DonutWithLegend({ data, nameKey, valueKey }) {
    const total = data.reduce((sum, d) => sum + (d[valueKey] || 0), 0);
    return (
        <div>
            <ResponsiveContainer width="100%" height={150}>
                <PieChart>
                    <Pie data={data} dataKey={valueKey} nameKey={nameKey} innerRadius={40} outerRadius={62} paddingAngle={2}>
                        {data.map((_, i) => (
                            <Cell key={i} fill={SERIES[i % SERIES.length]} stroke={colors.surface} strokeWidth={2} />
                        ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} />
                </PieChart>
            </ResponsiveContainer>
            <div style={s.legendWrap}>
                {data.map((d, i) => (
                    <div key={i} style={s.legendItem}>
                        <span style={{ ...s.legendDot, background: SERIES[i % SERIES.length] }} />
                        <span style={s.legendLabel}>{d[nameKey] || 'Unknown'}</span>
                        <span style={s.legendValue}>
                            {total ? Math.round((d[valueKey] / total) * 100) : 0}%
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function formatTime(iso) {
    if (!iso) return '–';
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const tooltipStyle = {
    background: colors.surfaceRaised,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    fontSize: 12,
    color: colors.textPrimary,
};
const tooltipItemStyle = { color: colors.textPrimary };
const tooltipLabelStyle = { color: colors.textSecondary };

const s = {
    page: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif",
        background: colors.bg,
        color: colors.textPrimary,
        minHeight: '100vh',
        padding: '32px 36px',
    },
    loadingText: { color: colors.textSecondary, fontSize: 14 },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 28,
        flexWrap: 'wrap',
        gap: 12,
    },
    headerRight: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
    },
    h1: { fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: '-0.01em' },
    subtitle: { fontSize: 13, color: colors.textSecondary, margin: '4px 0 0' },
    datePicker: {
        background: colors.surfaceRaised,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        padding: '7px 10px',
        color: colors.textPrimary,
        fontSize: 13,
        fontFamily: "'Inter', sans-serif",
        colorScheme: 'dark',
    },
    liveBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 12,
        color: colors.accent,
        background: 'rgba(45, 212, 191, 0.08)',
        border: `1px solid ${colors.accentDim}`,
        borderRadius: 20,
        padding: '6px 12px',
        whiteSpace: 'nowrap',
    },
    livePulse: {
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: colors.accent,
        display: 'inline-block',
        boxShadow: `0 0 0 3px rgba(45, 212, 191, 0.15)`,
    },
    kpiRow: { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' },
    card: {
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 10,
        padding: '18px 22px',
        minWidth: 170,
        flex: 1,
    },
    cardValue: { fontSize: 28, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: colors.textPrimary },
    cardLabel: { fontSize: 12.5, color: colors.textSecondary, marginTop: 4 },
    chartsRow: { display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'stretch' },
    panel: {
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 10,
        padding: 18,
        minWidth: 280,
        color: colors.textPrimary,
    },
    panelTitle: { fontSize: 13, fontWeight: 500, color: colors.textSecondary, margin: '0 0 14px' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 12.5 },
    th: {
        textAlign: 'left',
        padding: '6px 8px 8px 0',
        borderBottom: `1px solid ${colors.border}`,
        color: colors.textMuted,
        fontWeight: 500,
        fontSize: 11.5,
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
    },
    td: {
        textAlign: 'left',
        padding: '9px 8px 9px 0',
        borderBottom: `1px solid ${colors.border}`,
        color: colors.textPrimary,
    },
    mono: { fontFamily: "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace", fontSize: 12 },
    emptyRow: { padding: '18px 0', color: colors.textMuted, textAlign: 'center', fontSize: 12.5 },
    scrollBox: { maxHeight: 280, overflowY: 'auto', paddingRight: 10, marginRight: -10 },
    legendWrap: { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 },
    legendItem: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 },
    legendDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
    legendLabel: { color: colors.textSecondary, flex: 1 },
    legendValue: { color: colors.textPrimary, fontVariantNumeric: 'tabular-nums', fontWeight: 500 },
    siteRow: { marginBottom: 12 },
    siteRowTop: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: 5,
        fontSize: 12.5,
    },
    siteName: { color: colors.textPrimary, fontWeight: 500 },
    siteCount: {
        color: colors.textSecondary,
        fontVariantNumeric: 'tabular-nums',
        fontFamily: "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace",
    },
    siteBarTrack: {
        height: 6,
        borderRadius: 3,
        background: colors.surfaceRaised,
        overflow: 'hidden',
    },
    siteBarFill: {
        height: '100%',
        borderRadius: 3,
        background: colors.accent,
    },
};